/**
 * Tool implementations for Bandmate's WebMCP surface (SPEC §5). Every tool in
 * `tools/schemas.ts` gets an `execute` here; `createTools()` pairs each with
 * its frozen name / description / schema / annotations into a `ToolDef` for
 * `webmcp/register.ts`, and `tools/groups.ts` registers them per app mode.
 *
 * Rules every executor follows:
 * - Results are plain JSON objects and never throw: internal failures become
 *   `{ error: { code: 'execution_failed', message } }`, an aborted signal
 *   `{ error: { code: 'aborted' } }`. Readers stay ≤ 700 / 1500 characters
 *   (see `tools/format.ts`); no result exceeds 1500.
 * - Writers refuse with `busy` while an audition is open or recording is
 *   armed; they pre-check with `store.dryRun()` and ask the human (modal
 *   `askHuman`) before removing human-authored notes, returning
 *   `{ applied: false, reason: 'human_declined' }` when the human keeps
 *   their notes. Every write is applied `by: 'agent'` with a ledger label,
 *   calls `ctx.summarize()` and records `ctx.recordUndo(() => store.undoTo(id))`.
 * - `audition` builds one variant `Session` per option through the pure
 *   `applyMutation`, previews them through the store's transient layer, and
 *   commits the kept option as ONE logical agent action (sequential applies,
 *   undo to the first id). Stop, `load` and the abort signal dismiss it.
 * - `export_midi` never triggers a download: it arms the header Download
 *   button (`href` + `download`) and spotlights it.
 */
import type { Controller, Engine } from '../app/controller';
import { applyMutation } from '../state/session';
import type { ApplyResult, DrumHitInput, Mutation, NoteInput, Store } from '../state/store-api';
import {
  ACCEPTED_CHORD_FORMS,
  bassNotes,
  chordRhythmNotes,
  describeNotes,
  detectKey,
  inferRootsFromChordNotes,
  isChordParseError,
  keyName,
  mod12,
  parseChordSymbol,
  pitchToName,
  resolveNoteToken,
  suggestChords,
  voice,
  type BarRoot,
  type ParsedChord,
} from '../music/theory';
import { STYLE_DEFAULTS, drumPatternAllBars, kickSteps, parsePattern16 } from '../music/patterns';
import { buildSMF } from '../midi/smf';
import type { AuditionCardHandle } from '../ui/audition-card';
import { askHuman, type AskOutcome, type AskParams } from '../webmcp/ask';
import type { JSONSchema, ToolContext, ToolDef } from '../webmcp/register';
import { spotlight } from '../webmcp/spotlight';
import {
  BARS,
  TRACK_RANGE,
  cloneSession,
  joinStep,
  normalizeTonic,
  type BassPattern,
  type ChordRhythm,
  type ChordSlot,
  type DrumLane,
  type Key,
  type MelodicTrackId,
  type Note,
  type Session,
  type TrackId,
} from '../types';
import { describeLanes, formatSession, formatTrack, normalizeBars } from './format';
import { TOOLS, TOOL_NAMES, type AuditionChanges, type ToolInputs, type ToolName } from './schemas';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Everything the tools need from the app; `ask` and `now` are injectable for tests. */
export interface ToolDeps {
  store: Store;
  engine: Engine;
  controller: Controller;
  /** The docked audition card from `mountAuditionCard`. */
  auditionCard: AuditionCardHandle;
  /** Header Download MIDI button (spotlit by `export_midi`). */
  downloadButton(): HTMLButtonElement;
  /** Transport Play button (spotlit when audio is still locked). */
  playButton(): HTMLButtonElement;
  /** Confirmation dialog; defaults to `webmcp/ask.askHuman`. */
  ask?: typeof askHuman;
  /** Clock in ms; defaults to `Date.now`. */
  now?: () => number;
}

/** Structured tool failure (SPEC §5 `{ error: { code, message, issues?, hint?, next? } }`). */
export interface ToolFailure {
  error: {
    code: string;
    message: string;
    issues?: string[];
    hint?: string;
    next?: string;
    audition_id?: string;
  };
}

/** The tools built by `createTools`. */
export interface CreatedTools {
  defs: Record<ToolName, ToolDef>;
  /** Cancel an open audition, release object URLs. Idempotent. */
  dispose(): void;
}

/** One executor: typed input in, plain JSON out. May throw; `createTools` wraps it. */
export type Executor<N extends ToolName> = (input: ToolInputs[N], ctx: ToolContext) => Promise<unknown> | unknown;

/** All executors keyed by tool name. */
export type Executors = { [N in ToolName]: Executor<N> };

/** What the human sees when a writer would remove their notes and they keep them. */
export const HUMAN_DECLINED = Object.freeze({ applied: false, reason: 'human_declined' } as const);

/** How long a confirmation dialog waits before counting as declined. */
export const CONFIRM_TIMEOUT_MS = 60_000;
/** Default `audition.wait_seconds`. */
export const AUDITION_DEFAULT_WAIT_S = 25;
/** How often the audition checks whether the transport was stopped. */
export const STOP_POLL_MS = 200;
/** Text returned with `awaiting_human`. */
export const AWAITING_NEXT = 'card stays open; call get_session — audition.chosen shows the pick';
/** Text returned by `export_midi`. */
export const EXPORT_NEXT = 'Ask the human to click the highlighted Download button, then drag the file into GarageBand/Ableton/Logic';
/** Text returned by `transport` before audio is unlocked. */
export const MUTED_NEXT = 'Ask the human to press ▶ Play once';
/** Hint returned when the top undo entry is the human's own work. */
export const UNDO_HUMAN_HINT = 'the human can undo their own actions with Cmd+Z';

const LETTERS = ['A', 'B', 'C'] as const;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** Build a structured failure. */
export function fail(code: string, message: string, extra: Omit<ToolFailure['error'], 'code' | 'message'> = {}): ToolFailure {
  const error: ToolFailure['error'] = { code, message };
  if (extra.issues) error.issues = extra.issues;
  if (extra.hint) error.hint = extra.hint;
  if (extra.next) error.next = extra.next;
  if (extra.audition_id) error.audition_id = extra.audition_id;
  return { error };
}

function isFailure(x: unknown): x is ToolFailure {
  return !!x && typeof x === 'object' && 'error' in (x as object);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

function unitFor(track: TrackId): string {
  return track === 'drums' ? 'hit' : 'note';
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return typeof e === 'string' ? e : String(e);
}

function isAbortError(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as { name?: unknown }).name === 'AbortError';
}

function uniq<T>(items: T[]): T[] {
  return [...new Set(items)];
}

function contentCounts(s: Session, track: TrackId): { human: number; agent: number } {
  const items: ReadonlyArray<{ by: string }> = track === 'drums' ? s.tracks.drums.hits : s.tracks[track].notes;
  let human = 0;
  let agent = 0;
  for (const it of items) {
    if (it.by === 'human') human += 1;
    else agent += 1;
  }
  return { human, agent };
}

function grooveSummary(s: Session): string {
  return `${keyName(s.key)} · ${s.tempo} bpm · swing ${s.swing} · ${s.style}`;
}

function wrapIntoRange(pitch: number, range: readonly [number, number]): number {
  let p = pitch;
  while (p < range[0]) p += 12;
  while (p > range[1]) p -= 12;
  return p;
}

/** Sanitise an `export_midi` filename to `[A-Za-z0-9_-]{1,40}`; empty → `bandmate`. */
export function sanitizeFilename(name: string | undefined): string {
  const cleaned = (name ?? '')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return cleaned || 'bandmate';
}

function bytesToDataUrl(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x2000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x2000));
  }
  return `data:audio/midi;base64,${btoa(binary)}`;
}

/** Deep copy of a frozen `ObjectSchema` as the mutable `JSONSchema` the registry wants. */
export function toJSONSchema(schema: unknown): JSONSchema {
  return JSON.parse(JSON.stringify(schema)) as JSONSchema;
}

// ---------------------------------------------------------------------------
// Musical building blocks shared by writers and audition
// ---------------------------------------------------------------------------

/** A parsed, voiced, articulated progression ready for `setProgression`. */
export interface BuiltProgression {
  parsed: ParsedChord[];
  slots: ChordSlot[];
  notes: NoteInput[];
  rhythm: ChordRhythm;
}

/** Repeat 1 or 2 symbols to fill 4 bars; 3 is an error; 4 stays. */
function fillBars(symbols: string[]): string[] | ToolFailure {
  const list = symbols.slice(0, BARS);
  if (list.length === 1) return [list[0]!, list[0]!, list[0]!, list[0]!];
  if (list.length === 2) return [list[0]!, list[1]!, list[0]!, list[1]!];
  if (list.length === 3) {
    return fail('invalid_progression', 'give 1, 2 or 4 chords: 1 or 2 repeat to fill 4 bars, 4 map one per bar; 3 cannot be spread evenly', {
      hint: 'repeat one chord to make it 4, e.g. ["i","VI","III","III"]',
    });
  }
  if (list.length === 0) return fail('invalid_progression', 'progression must hold at least one chord');
  return list;
}

/** Parse + voice + articulate a progression in `key`. */
export function buildProgression(symbols: string[], key: Key, rhythm: ChordRhythm): BuiltProgression | ToolFailure {
  const filled = fillBars(symbols);
  if (isFailure(filled)) return filled;
  const parsed: ParsedChord[] = [];
  const issues: string[] = [];
  for (const sym of filled) {
    const r = parseChordSymbol(sym, key);
    if (isChordParseError(r)) issues.push(`${sym}: ${r.error}`);
    else parsed.push(r);
  }
  if (issues.length) {
    return fail('invalid_chord', `could not read ${issues.length === 1 ? 'a chord' : `${issues.length} chords`} in ${keyName(key)}`, {
      issues: uniq(issues),
      hint: `accepted forms: ${ACCEPTED_CHORD_FORMS.join(', ')}`,
    });
  }
  const voicings = voice(parsed);
  const slots: ChordSlot[] = parsed.map((p, bar) => ({
    bar,
    numeral: p.numeral,
    name: p.name,
    pitches: voicings[bar] ?? [],
    borrowed: p.borrowed,
    altered: p.altered,
  }));
  const notes = chordRhythmNotes(voicings, rhythm);
  return { parsed, slots, notes, rhythm };
}

/** One sentence on what the progression is made of. */
export function explainProgression(parsed: ParsedChord[], key: Key): string {
  const numerals = parsed.map((p) => p.numeral).join('–');
  const parts: string[] = [];
  const altered = uniq(parsed.filter((p) => p.altered).map((p) => p.numeral));
  const borrowed = uniq(parsed.filter((p) => p.borrowed).map((p) => p.numeral));
  if (altered.length) parts.push(`${altered.join(', ')} ${altered.length === 1 ? 'comes' : 'come'} from harmonic minor`);
  if (borrowed.length) parts.push(`${borrowed.join(', ')} ${borrowed.length === 1 ? 'is' : 'are'} borrowed from outside the key`);
  parts.push(...uniq(parsed.filter((p) => p.normalizedFrom).map((p) => `${p.normalizedFrom} is written ${p.numeral} here`)));
  parts.push(...uniq(parsed.flatMap((p) => (p.hint ? [p.hint] : []))));
  return `${numerals} in ${keyName(key)}${parts.length ? `: ${parts.join('; ')}` : ', all diatonic'}.`;
}

/**
 * Re-read stored progression slots in `key`: numeral, name and the
 * borrowed/altered flags are derived again from each slot's chord name (the
 * key-independent spelling; the store transposes it with the content), pitches
 * are kept. Returns null when any slot no longer parses, so the caller drops
 * the metadata instead of keeping stale numerals (`set_groove` key changes).
 */
export function rederiveProgression(slots: readonly ChordSlot[], key: Key): ChordSlot[] | null {
  const out: ChordSlot[] = [];
  for (const slot of slots) {
    const r = parseChordSymbol(slot.name, key);
    if (isChordParseError(r)) return null;
    out.push({ ...slot, pitches: [...slot.pitches], numeral: r.numeral, name: r.name, borrowed: r.borrowed, altered: r.altered });
  }
  return out;
}

/** Per-bar roots for the bass: the progression metadata when present, else inferred from the chord notes. */
export function rootsFor(s: Session): BarRoot[] | null {
  if (s.progression && s.progression.length > 0) {
    const roots: BarRoot[] = [];
    for (const slot of s.progression) {
      if (slot.bar < 0 || slot.bar >= BARS) continue;
      let parsed: ParsedChord | null = null;
      for (const text of [slot.numeral, slot.name]) {
        const r = parseChordSymbol(text, s.key);
        if (!isChordParseError(r)) {
          parsed = r;
          break;
        }
      }
      if (parsed) {
        roots.push({
          bar: slot.bar,
          rootPc: parsed.rootPc,
          thirdPc: mod12(parsed.rootPc + (parsed.intervals[1] ?? 3)),
          fifthPc: mod12(parsed.rootPc + (parsed.intervals[2] ?? 7)),
        });
      } else if (slot.pitches.length) {
        const notes: Note[] = slot.pitches.map((pitch) => ({ step: slot.bar * 16, pitch, length: 16, velocity: 0.8, by: 'agent' }));
        const inferred = inferRootsFromChordNotes(notes);
        const hit = inferred?.find((r) => r.bar === slot.bar);
        if (hit) roots.push(hit);
      }
    }
    if (roots.length) return roots;
  }
  return inferRootsFromChordNotes(s.tracks.chords.notes);
}

/** A generated bass line ready for `setBassPattern`. */
export interface BuiltBass {
  notes: NoteInput[];
  roots: BarRoot[];
  /** Set when `follow_kick` had no kicks and fell back to `root`. */
  fallback?: 'root';
}

/** Generate the bass for a pattern on a session's progression. */
export function buildBass(s: Session, pattern: BassPattern, octave: 1 | 2): BuiltBass | ToolFailure {
  const roots = rootsFor(s);
  if (!roots) return fail('needs_chords', 'the chords track is empty, so there are no roots to follow', { next: 'call add_chords first' });
  const kicks = kickSteps(s.tracks.drums.hits);
  const notes = bassNotes(roots, pattern, { octaveShift: octave === 1 ? -12 : 0, kickSteps: kicks, style: s.style });
  const out: BuiltBass = { notes, roots };
  if (pattern === 'follow_kick' && kicks.length === 0) out.fallback = 'root';
  return out;
}

/** One audition variant: the preview session plus the mutations that reproduce it on the committed store. */
export interface Variant {
  session: Session;
  mutations: { m: Mutation; label: string }[];
}

/** Build a variant by applying an option's changes to a clone of `base` through the pure `applyMutation`. */
export function buildVariant(base: Session, changes: AuditionChanges, optionLabel: string): Variant | ToolFailure {
  let cur = cloneSession(base);
  const mutations: Variant['mutations'] = [];
  const push = (m: Mutation, label: string) => {
    cur = applyMutation(cur, m, 'agent').next;
    mutations.push({ m, label });
  };
  const bad = (message: string) => fail('invalid_option', `option "${optionLabel}": ${message}`);

  if (changes.tempo !== undefined || changes.swing !== undefined) {
    const m: Mutation = { type: 'setGroove' };
    const what: string[] = [];
    if (changes.tempo !== undefined) {
      m.tempo = changes.tempo;
      what.push(`tempo ${changes.tempo}`);
    }
    if (changes.swing !== undefined) {
      m.swing = changes.swing;
      what.push(`swing ${changes.swing}`);
    }
    push(m, `set ${what.join(', ')}`);
  }
  if (changes.progression) {
    const rhythm = changes.rhythm ?? cur.progressionRhythm ?? STYLE_DEFAULTS[cur.style].chordRhythm;
    const built = buildProgression(changes.progression, cur.key, rhythm);
    if (isFailure(built)) return bad(`${built.error.message}${built.error.issues ? ` (${built.error.issues.join('; ')})` : ''}`);
    push({ type: 'setProgression', slots: built.slots, rhythm, notes: built.notes }, `added chords ${built.parsed.map((p) => p.numeral).join('–')}`);
  } else if (changes.rhythm) {
    if (!cur.progression || cur.progression.length === 0) return bad('a rhythm needs a chord progression; call add_chords first');
    const notes = chordRhythmNotes(cur.progression, changes.rhythm);
    push({ type: 'setProgression', slots: cur.progression, rhythm: changes.rhythm, notes }, `set chord rhythm ${changes.rhythm}`);
  }
  if (changes.bass_pattern) {
    const built = buildBass(cur, changes.bass_pattern, 2);
    if (isFailure(built)) return bad(`${built.error.message}; ${built.error.next ?? ''}`.trim());
    push({ type: 'setBassPattern', pattern: changes.bass_pattern, notes: built.notes }, `added ${changes.bass_pattern} bass`);
  }
  if (changes.drum_density) {
    const hits: DrumHitInput[] = drumPatternAllBars(cur.style, changes.drum_density, true);
    push({ type: 'setDrumHits', hits, mode: 'replace' }, `added ${cur.style} ${changes.drum_density} drums`);
  }
  if (mutations.length === 0) return bad('changes nothing; give at least one of progression, rhythm, bass_pattern, drum_density, tempo, swing');
  return { session: cur, mutations };
}

// ---------------------------------------------------------------------------
// Executors
// ---------------------------------------------------------------------------

/** Build the raw executors (may throw; `createTools` adds the never-throw wrapper). */
export function createExecutors(deps: ToolDeps): { exec: Executors; dispose(): void } {
  const { store, engine, controller, auditionCard } = deps;
  const ask = deps.ask ?? askHuman;
  const now = deps.now ?? Date.now;

  let auditionSeq = 0;
  let cancelAudition: (() => void) | null = null;
  let lastObjectUrl: string | null = null;

  // ---------- guards ----------

  const busy = (): ToolFailure | null => {
    const a = store.audition();
    if (store.mode() === 'auditioning' || a?.awaiting) {
      return fail('busy', 'an audition is playing; wait for the human to choose', a ? { audition_id: a.id } : {});
    }
    if (store.mode() === 'recording') return fail('busy', 'recording is armed; call stop_recording first');
    return null;
  };

  interface ConfirmParams {
    question: string;
    detail?: string;
    yes: { id: string; label: string };
    no: { id: string; label: string };
    signal?: AbortSignal;
  }

  const confirm = async (p: ConfirmParams): Promise<boolean> => {
    const params: AskParams = {
      question: p.question,
      options: [p.yes, p.no],
      dismissible: true,
      timeoutMs: CONFIRM_TIMEOUT_MS,
    };
    if (p.detail) params.detail = p.detail;
    if (p.signal) params.signal = p.signal;
    const outcome: AskOutcome = await ask(params);
    const ok = outcome === p.yes.id;
    store.logHuman({ kind: ok ? 'confirm' : 'decline', detail: `${ok ? 'confirmed' : 'declined'}: ${p.question}` });
    return ok;
  };

  /** Ask before a mutation removes human notes/hits; null means go ahead. */
  const guardHuman = async (m: Mutation, track: TrackId, ctx: ToolContext, lane?: DrumLane): Promise<typeof HUMAN_DECLINED | null> => {
    const { removedHuman } = store.dryRun(m);
    if (removedHuman < 1) return null;
    const question = lane ? `Replace your ${plural(removedHuman, `${lane} hit`)}?` : `Replace your ${plural(removedHuman, unitFor(track))} on ${track}?`;
    const ok = await confirm({
      question,
      detail: 'The agent wants to overwrite notes you wrote by hand. Replacing is undoable.',
      yes: { id: 'replace', label: 'Replace' },
      no: { id: 'keep', label: 'Keep mine' },
      signal: ctx.signal,
    });
    return ok ? null : HUMAN_DECLINED;
  };

  /** Apply as the agent, summarise, and record the undo closure. */
  const commit = (m: Mutation, label: string, summary: string, ctx: ToolContext): ApplyResult => {
    const r = store.apply(m, { by: 'agent', label });
    ctx.summarize(summary);
    const id = r.id;
    if (id) {
      ctx.recordUndo(() => {
        store.undoTo(id);
      }, label);
    }
    return r;
  };

  const position = () => {
    const p = engine.position();
    return { bar: p.bar + 1, step: p.step + 1 };
  };

  const transportState = (audio: 'ready' | 'muted_until_user_taps') => ({
    playing: engine.isPlaying(),
    position: position(),
    audio,
    loop_seconds: Math.round(engine.loopSeconds() * 100) / 100,
    bar_seconds: Math.round(engine.barSeconds() * 100) / 100,
  });

  // ---------- readers ----------

  const get_session: Executor<'get_session'> = (_input, ctx) => {
    const s = formatSession({ store, engine, controller, now });
    ctx.summarize(`read session: ${s.key} · ${s.tempo} bpm · ${s.mode}`);
    return s;
  };

  const get_track: Executor<'get_track'> = (input, ctx) => {
    const t = formatTrack(store.get(), input);
    ctx.summarize(`read ${t.track} (${t.human} human, ${t.agent} agent)`);
    return t;
  };

  const suggest_chords: Executor<'suggest_chords'> = (input, ctx) => {
    const s = store.get();
    const lead = s.tracks.lead.notes;
    if (lead.length === 0) {
      return fail('no_melody', 'the lead track is empty; there is nothing to harmonise', {
        hint: 'ask the human to play a few notes or use write_notes',
      });
    }
    const candidates = detectKey(lead, { sessionKey: s.key });
    const detected = candidates[0]?.key ?? s.key;
    const n = Math.min(5, Math.max(1, Math.floor(input.n ?? 3)));
    // Numerals are ALWAYS relative to the SESSION key: add_chords parses them
    // against the session key, so suggestions in a detected key would land on
    // the wrong chords. Detected keys are reported separately.
    const suggestions = suggestChords(lead, s.key, { n, style: s.style });
    const mismatch = detected.tonic !== s.key.tonic || detected.mode !== s.key.mode;
    ctx.summarize(
      `suggested ${suggestions.length} progressions in ${keyName(s.key)}${mismatch ? ` (melody sits in ${keyName(detected)})` : ''}`,
    );
    const out: Record<string, unknown> = {
      key: keyName(s.key),
      key_candidates: candidates.map((c) => ({ key: keyName(c.key), confidence: c.confidence })),
      // The mismatch block below costs ~250 chars; trim the melody to stay under the 1500-char result budget.
      melody: describeNotes(lead, s.key, { maxChars: mismatch ? 200 : 320 }),
      suggestions,
      next: 'call add_chords with one of these',
    };
    if (mismatch) {
      const inDetected = suggestChords(lead, detected, { n: 1, style: s.style })[0];
      const names = inDetected?.names ?? [];
      out.session_key = keyName(s.key);
      out.detected_key = keyName(detected);
      out.key_mismatch = true;
      out.next =
        `session key is ${keyName(s.key)} but the melody sits in ${keyName(detected)}: ` +
        `call set_groove {key:'${detected.tonic}', mode:'${detected.mode}'} first` +
        (names.length ? `, or pass chord NAMES (${names.join(', ')}) to add_chords` : '');
    }
    return out;
  };

  // ---------- writers ----------

  const set_groove: Executor<'set_groove'> = (input, ctx) => {
    const b = busy();
    if (b) return b;
    const s = store.get();
    const changed: string[] = [];
    const groove: Mutation = { type: 'setGroove' };
    if (input.tempo !== undefined && input.tempo !== s.tempo) {
      groove.tempo = input.tempo;
      changed.push('tempo');
    }
    if (input.swing !== undefined && input.swing !== s.swing) {
      groove.swing = input.swing;
      changed.push('swing');
    }
    if (input.style !== undefined && input.style !== s.style) {
      groove.style = input.style;
      changed.push('style');
    }
    if (input.metronome !== undefined && input.metronome !== s.metronome) {
      groove.metronome = input.metronome;
      changed.push('metronome');
    }
    let keyChange: Mutation | null = null;
    if (input.key !== undefined || input.mode !== undefined) {
      const tonic = input.key !== undefined ? normalizeTonic(input.key) : s.key.tonic;
      if (!tonic) return fail('invalid_key', `unknown key spelling "${input.key}"`, { hint: 'use C, C#, Db, D … B' });
      const mode = input.mode ?? s.key.mode;
      if (tonic !== s.key.tonic || mode !== s.key.mode) {
        keyChange = { type: 'setKey', tonic, mode, transposeContent: input.transpose_content ?? true };
        changed.push('key');
      }
    }
    if (changed.length === 0) return { applied: false, reason: 'nothing_to_change', summary: grooveSummary(s), changed };

    let firstId: string | null = null;
    let transposed: ApplyResult['transposed'];
    const label = `set ${changed.join(', ')}`;
    if (changed.some((c) => c !== 'key')) {
      const r = store.apply(groove, { by: 'agent', label });
      firstId ??= r.id;
    }
    let progression: string[] | null | undefined;
    let note: string | undefined;
    if (keyChange) {
      const before = store.get();
      if (before.progression && before.progression.length > 0) {
        // The stored numerals and borrowed/altered flags are relative to the OLD
        // key. Re-derive them from the chord names in the new key and commit
        // them together with the key change as ONE entry, so undo_id reverts both.
        const outcome = applyMutation(before, keyChange, 'agent');
        const next = outcome.next;
        const rederived = rederiveProgression(next.progression ?? [], next.key);
        if (rederived) next.progression = rederived;
        else {
          next.progression = null;
          next.progressionRhythm = null;
          note = `progression metadata dropped: its chords no longer parse in ${keyName(next.key)}; the chord notes are unchanged`;
        }
        const r = store.apply({ type: 'replaceSession', session: next }, { by: 'agent', label });
        firstId ??= r.id;
        if (outcome.transposed) transposed = outcome.transposed;
        progression = next.progression ? next.progression.map((slot) => slot.numeral) : null;
      } else {
        const r = store.apply(keyChange, { by: 'agent', label });
        firstId ??= r.id;
        if (r.transposed) transposed = r.transposed;
      }
    }
    const summary = grooveSummary(store.get());
    ctx.summarize(summary);
    const undoId = firstId;
    if (undoId) {
      ctx.recordUndo(() => {
        store.undoTo(undoId);
      }, label);
    }
    const after = store.get();
    const out: Record<string, unknown> = {
      applied: true,
      summary,
      changed,
      key: keyName(after.key),
      tempo: after.tempo,
      swing: after.swing,
      style: after.style,
      metronome: after.metronome,
      undo_id: undoId,
    };
    // Never misreport a clamp: name every field whose applied value differs from the input.
    const clamped: string[] = [];
    if (input.tempo !== undefined && after.tempo !== input.tempo) clamped.push('tempo');
    if (input.swing !== undefined && after.swing !== input.swing) clamped.push('swing');
    if (clamped.length) out.clamped = clamped;
    if (transposed) out.transposed = transposed;
    if (progression !== undefined) out.progression = progression;
    if (note) out.note = note;
    return out;
  };

  const add_chords: Executor<'add_chords'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    const s = store.get();
    const rhythm = input.rhythm ?? STYLE_DEFAULTS[s.style].chordRhythm;
    const built = buildProgression(input.progression, s.key, rhythm);
    if (isFailure(built)) return built;
    const m: Mutation = { type: 'setProgression', slots: built.slots, rhythm, notes: built.notes };
    const declined = await guardHuman(m, 'chords', ctx);
    if (declined) return declined;
    const numerals = built.parsed.map((p) => p.numeral);
    const label = `added chords ${numerals.join('–')}`;
    const summary = `${label} (${built.parsed.map((p) => p.name).join(' ')}) · ${rhythm}`;
    const r = commit(m, label, summary, ctx);
    const normalized: Record<string, string> = {};
    for (const p of built.parsed) if (p.normalizedFrom) normalized[p.normalizedFrom] = p.numeral;
    const borrowedCount = built.parsed.filter((p) => p.borrowed).length;
    const out: Record<string, unknown> = {
      applied: true,
      chords: built.slots.map((slot) => ({ bar: slot.bar + 1, numeral: slot.numeral, name: slot.name, notes: slot.pitches.map(pitchToName) })),
      rhythm,
      altered: uniq(built.parsed.filter((p) => p.altered).map((p) => p.numeral)),
      borrowed: uniq(built.parsed.filter((p) => p.borrowed).map((p) => p.numeral)),
      normalized,
      explanation: explainProgression(built.parsed, s.key),
      undo_id: r.id,
      summary,
    };
    // Mostly-borrowed progressions usually mean the numerals were meant for
    // another key (see suggest_chords.key_mismatch), not deliberate colour.
    if (borrowedCount >= 3) out.warning = `most of these chords are outside ${keyName(s.key)}; did you mean set_groove first?`;
    return out;
  };

  const add_bass: Executor<'add_bass'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    const s = store.get();
    const pattern = input.pattern ?? 'root';
    const octave = input.octave === 1 ? 1 : 2;
    const built = buildBass(s, pattern, octave);
    if (isFailure(built)) return built;
    const m: Mutation = { type: 'setBassPattern', pattern, notes: built.notes };
    const declined = await guardHuman(m, 'bass', ctx);
    if (declined) return declined;
    const label = `added ${pattern} bass${octave === 1 ? ' (low octave)' : ''}`;
    const summary = `${label}: ${plural(built.notes.length, 'note')}`;
    const r = commit(m, label, summary, ctx);
    const out: Record<string, unknown> = {
      applied: true,
      pattern,
      octave,
      notes: describeNotes(
        built.notes.map((n) => ({ ...n, by: 'agent' as const })),
        s.key,
        { maxChars: 600 },
      ),
      undo_id: r.id,
      summary,
    };
    if (built.fallback) out.fallback = built.fallback;
    return out;
  };

  const add_drums: Executor<'add_drums'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    const s = store.get();
    const style = input.style ?? s.style;
    const density = input.density ?? 'medium';
    const fill = input.fill ?? true;
    const hits: DrumHitInput[] = drumPatternAllBars(style, density, fill);
    const m: Mutation = { type: 'setDrumHits', hits, mode: 'replace' };
    const declined = await guardHuman(m, 'drums', ctx);
    if (declined) return declined;
    const label = `added ${style} ${density} drums${fill ? ' with fill' : ''}`;
    const summary = `${label}: ${plural(hits.length, 'hit')}`;
    const r = commit(m, label, summary, ctx);
    const lanes: Record<string, string | Record<string, string>> = {};
    for (const [lane, view] of Object.entries(describeLanes(hits, [1, 2, 3, 4]))) {
      lanes[lane] = 'all' in view ? view.all : view;
    }
    return { applied: true, style, density, fill, lanes, undo_id: r.id, summary };
  };

  const write_notes: Executor<'write_notes'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    const s = store.get();
    const track: MelodicTrackId = input.track;
    const range = TRACK_RANGE[track];
    const notes: NoteInput[] = [];
    const issues: string[] = [];
    const out_of_key: string[] = [];
    const clamped: string[] = [];
    for (const spec of input.notes) {
      const r = resolveNoteToken(spec.note, s.key, track, spec.octave);
      if ('error' in r) {
        issues.push(r.error);
        continue;
      }
      const where = `${spec.note}@b${spec.bar}.${spec.step}`;
      let pitch = r.pitch;
      if (pitch < range[0] || pitch > range[1]) {
        pitch = wrapIntoRange(pitch, range);
        clamped.push(`${where}→${pitchToName(pitch)}`);
      }
      if (r.outOfKey) out_of_key.push(where);
      notes.push({
        step: joinStep(Math.min(BARS, Math.max(1, spec.bar)) - 1, Math.min(16, Math.max(1, spec.step)) - 1),
        pitch,
        length: spec.length ?? 1,
        velocity: spec.velocity ?? 0.8,
      });
    }
    if (issues.length) return fail('invalid_note', `${issues.length} of ${input.notes.length} notes could not be read`, { issues });
    const mode = input.mode ?? 'merge';
    const m: Mutation = { type: 'writeNotes', track, notes, mode };
    if (mode === 'replace') {
      const declined = await guardHuman(m, track, ctx);
      if (declined) return declined;
    }
    const label = `${mode === 'replace' ? 'replaced' : 'wrote'} ${plural(notes.length, 'note')} on ${track}`;
    const r = commit(m, label, label, ctx);
    return { applied: true, track, mode, written: r.written, out_of_key, clamped, undo_id: r.id, summary: label };
  };

  const set_drum_steps: Executor<'set_drum_steps'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    const bars = normalizeBars(input.bars);
    const hits: DrumHitInput[] = [];
    for (const bar of bars) {
      const parsed = parsePattern16(input.pattern, input.lane, bar - 1);
      if ('error' in parsed) return fail('invalid_pattern', parsed.error);
      hits.push(...parsed);
    }
    const m: Mutation = { type: 'setDrumHits', hits, mode: 'replace', bars: bars.map((x) => x - 1), lanes: [input.lane] };
    const declined = await guardHuman(m, 'drums', ctx, input.lane);
    if (declined) return declined;
    const label = `set ${input.lane} ${input.pattern}${bars.length === BARS ? '' : ` (bars ${bars.join(', ')})`}`;
    const r = commit(m, label, label, ctx);
    return { applied: true, lane: input.lane, pattern: input.pattern, bars, undo_id: r.id, summary: label };
  };

  const set_track: Executor<'set_track'> = (input, ctx) => {
    const b = busy();
    if (b) return b;
    const track = input.track;
    const parts: string[] = [];
    const m: Mutation = { type: 'setTrack', track };
    if (input.muted !== undefined) {
      m.muted = input.muted;
      parts.push(`${input.muted ? 'muted' : 'unmuted'} ${track}`);
    }
    if (input.solo !== undefined) {
      m.solo = input.solo;
      parts.push(`${input.solo ? 'soloed' : 'unsoloed'} ${track}`);
    }
    if (input.volume_db !== undefined) {
      m.volume = input.volume_db;
      parts.push(`set ${track} to ${input.volume_db} dB`);
    }
    const flagsOf = () => {
      const t = store.get().tracks[track];
      return { track, muted: t.muted, solo: t.solo, volume_db: t.volume };
    };
    if (parts.length === 0) return { applied: false, reason: 'nothing_to_change', ...flagsOf(), summary: `nothing to change on ${track}` };
    const label = parts.join(', ');
    const r = commit(m, label, label, ctx);
    const flags = flagsOf();
    const out: Record<string, unknown> = { applied: true, ...flags, undo_id: r.id, summary: label };
    if (input.volume_db !== undefined && flags.volume_db !== input.volume_db) out.clamped = ['volume_db'];
    return out;
  };

  const clear_track: Executor<'clear_track'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    const track = input.track;
    const counts = contentCounts(store.get(), track);
    if (counts.human + counts.agent === 0) {
      const summary = `${track} is already empty`;
      ctx.summarize(summary);
      return { applied: true, track, removed: { human: 0, agent: 0 }, undo_id: null, summary };
    }
    const unit = unitFor(track);
    const ok = await confirm({
      question: `Clear the ${track} (${plural(counts.human, `human ${unit}`)}, ${plural(counts.agent, `agent ${unit}`)})?`,
      detail: 'This removes everything on the track. It is undoable.',
      yes: { id: 'clear', label: 'Clear' },
      no: { id: 'keep', label: 'Keep' },
      signal: ctx.signal,
    });
    if (!ok) return HUMAN_DECLINED;
    const label = `cleared ${track}`;
    const summary = `${label}: ${plural(counts.human, `human ${unit}`)}, ${plural(counts.agent, `agent ${unit}`)}`;
    const r = commit({ type: 'clearTrack', track }, label, summary, ctx);
    return { applied: true, track, removed: { human: r.removedHuman, agent: r.removedAgent }, undo_id: r.id, summary };
  };

  // ---------- dialog ----------

  const audition: Executor<'audition'> = async (input, ctx) => {
    const b = busy();
    if (b) return b;
    if (ctx.signal?.aborted) return fail('aborted', 'audition cancelled before it started');
    const options = input.options;
    if (options.length < 2 || options.length > 3) {
      return fail('invalid_input', 'give 2 or 3 options to compare', { issues: [`options has ${options.length} items, must be 2..3`] });
    }
    const base = store.get();
    const variants: Variant[] = [];
    for (const o of options) {
      const v = buildVariant(base, o.changes ?? {}, o.label);
      if (isFailure(v)) return v;
      variants.push(v);
    }

    const seq = ++auditionSeq;
    const id = `a${seq}`;
    const labels = options.map((o) => o.label);
    const question = input.question?.trim() || 'Which one sounds better?';
    const waitMs = Math.min(60, Math.max(5, input.wait_seconds ?? AUDITION_DEFAULT_WAIT_S)) * 1000;

    // Sound must be unlocked before the card opens: a silent audition is
    // worse than none, so refuse and point the human at Play instead.
    if (!engine.isPlaying()) {
      const res = controller.play(undefined, { by: 'agent' });
      if (res.audio === 'muted_until_user_taps') {
        spotlight(deps.playButton(), { label: 'press Play to unlock sound', scroll: false });
        ctx.summarize('audition refused: sound is locked until the human presses Play');
        return fail('audio_locked', 'the human has not unlocked sound yet; an audition would be silent', {
          next: 'Ask the human to press ▶ Play once, then call audition again',
        });
      }
    }
    store.setMode('auditioning');
    store.setAudition({ id, question, labels, chosen: null, awaiting: true, startedAt: now() });

    let finished = false;
    let aborted = false;
    let keptIndex: number | null = null;
    let firstUndoId: string | null = null;
    let wasPlaying = engine.isPlaying();

    const unwatch = () => {
      clearInterval(poll);
      offLoad();
      ctx.signal?.removeEventListener('abort', onAbort);
      if (cancelAudition === cancel) cancelAudition = null;
    };
    const restore = (chosen: string | null) => {
      if (store.hasTransient()) store.setTransient(null);
      const a = store.audition();
      if (a && a.id === id) store.setAudition({ ...a, chosen, awaiting: false });
      if (store.mode() === 'auditioning') store.setMode('idle');
      unwatch();
    };
    const onKeep = (index: number) => {
      if (finished) return;
      finished = true;
      keptIndex = index;
      if (store.hasTransient()) store.setTransient(null);
      for (const { m, label } of variants[index]!.mutations) {
        const r = store.apply(m, { by: 'agent', label: `${label} (audition ${LETTERS[index]})` });
        firstUndoId ??= r.id;
      }
      restore(labels[index]!);
      store.logHuman({ kind: 'audition_choice', detail: `chose ${LETTERS[index]} (audition #${seq})` });
    };
    const onNeither = () => {
      if (finished) return;
      finished = true;
      restore(null);
      store.logHuman({ kind: 'audition_choice', detail: `chose neither (audition #${seq})` });
    };
    const onPreview = (index: number | 'current') => {
      if (finished) return;
      store.setTransient(index === 'current' ? null : variants[index]!.session);
    };
    /** Stop, load or abort: close the card and put everything back. */
    const cancel = () => {
      if (!finished) {
        finished = true;
        restore(null);
      }
      auditionCard.dismiss();
    };
    const onAbort = () => {
      aborted = true;
      cancel();
    };
    const poll = setInterval(() => {
      const playing = engine.isPlaying();
      if (playing) wasPlaying = true;
      else if (wasPlaying) cancel();
    }, STOP_POLL_MS);
    const offLoad = store.subscribe((_s, meta) => {
      if (meta.source === 'load') cancel();
    });
    ctx.signal?.addEventListener('abort', onAbort, { once: true });
    cancelAudition = cancel;

    const shown = auditionCard.show({
      auditionId: id,
      question,
      options: options.map((o) => (o.why ? { label: o.label, why: o.why } : { label: o.label })),
      onPreview,
      onKeep,
      onNeither,
      timeoutMs: waitMs,
    });
    // Start with option A sounding: press its ▶ so the card reflects it, else preview directly.
    const playA = auditionCard.el.querySelector<HTMLElement>('[data-play="0"]');
    if (playA && typeof playA.click === 'function') playA.click();
    else onPreview(0);

    const outcome = await shown;
    const listened: Record<string, boolean> = {};
    options.forEach((_o, i) => {
      listened[LETTERS[i]!] = outcome.listened[i] ?? false;
    });

    if (outcome.reason === 'timeout' && outcome.stillOpen) {
      ctx.summarize(`audition #${seq} waiting for the human (${labels.join(' / ')})`);
      return { status: 'awaiting_human', audition_id: id, listened, next: AWAITING_NEXT };
    }
    if (aborted) return fail('aborted', 'audition cancelled');
    if (outcome.reason === 'stopped') {
      cancel();
      ctx.summarize(`audition #${seq} stopped`);
      return { chosen: null, reason: 'stopped', applied: false, listened, audition_id: id };
    }
    if (outcome.chosen === null || keptIndex === null) {
      ctx.summarize(`audition #${seq}: the human kept neither`);
      return { chosen: null, reason: 'neither', applied: false, listened, audition_id: id };
    }
    const letter = LETTERS[keptIndex]!;
    const summary = `audition #${seq}: the human kept ${letter} (${labels[keptIndex]})`;
    ctx.summarize(summary);
    const undoId = firstUndoId;
    if (undoId) {
      ctx.recordUndo(() => {
        store.undoTo(undoId);
      }, `kept ${labels[keptIndex]}`);
    }
    return { chosen: letter, label: labels[keptIndex], applied: true, listened, undo_id: undoId, audition_id: id, audio: 'ready', summary };
  };

  const undo: Executor<'undo'> = (input, ctx) => {
    // Undo is a dialog tool (always registered), so it must refuse the same
    // way the writers do while an audition is open or a recording is armed.
    const b = busy();
    if (b) return b;
    const stack = store.undoStack();
    if (stack.length === 0) return { applied: false, reason: 'nothing_to_undo', summary: 'the undo stack is empty' };
    let result;
    if (input.undo_id !== undefined) {
      // An unknown id is an input error regardless of what sits on top.
      const index = stack.findIndex((e) => e.id === input.undo_id);
      if (index < 0) return fail('unknown_undo_id', `no undo entry "${input.undo_id}"`, { hint: 'use an undo_id returned by a writer, or steps' });
      const blocker = stack.slice(0, index + 1).find((e) => e.by === 'human');
      if (blocker) {
        return { applied: false, reason: 'human_action_in_range', blocking: { label: blocker.label }, hint: UNDO_HUMAN_HINT };
      }
      result = store.undoTo(input.undo_id);
    } else {
      const top = stack[0]!;
      if (top.by === 'human') {
        return { applied: false, reason: 'top_is_human_action', top: { label: top.label }, hint: UNDO_HUMAN_HINT };
      }
      const steps = Math.min(5, Math.max(1, Math.floor(input.steps ?? 1)));
      let count = 0;
      for (const e of stack) {
        if (e.by !== 'agent' || count >= steps) break;
        count += 1;
      }
      result = store.undo(count);
    }
    const undone = result.undone.map((e) => ({ undo_id: e.id, label: e.label }));
    const summary = `undid ${plural(undone.length, 'agent action')}${undone[0] ? `: ${undone.map((u) => u.label).join('; ')}` : ''}`;
    ctx.summarize(summary);
    return { applied: result.applied, undone, summary };
  };

  // ---------- transport ----------

  const transport: Executor<'transport'> = (input, ctx) => {
    if (input.action === 'stop') {
      controller.stop({ by: 'agent' });
      ctx.summarize('stopped the loop');
      return transportState(controller.audioReady() ? 'ready' : 'muted_until_user_taps');
    }
    const fromBar = input.from_bar !== undefined ? Math.min(BARS, Math.max(1, Math.round(input.from_bar))) - 1 : undefined;
    // Echo what was actually applied; `clamped` names any field that differs from the input.
    const echo: Record<string, unknown> = {};
    if (fromBar !== undefined) {
      echo.from_bar = fromBar + 1;
      if (fromBar + 1 !== input.from_bar) echo.clamped = ['from_bar'];
    }
    const res = controller.play(fromBar, { by: 'agent' });
    if (!res.playing && res.audio === 'muted_until_user_taps') {
      spotlight(deps.playButton(), { label: 'press Play to unlock sound', scroll: false });
      ctx.summarize('play requested; sound is off until the human taps');
      return { ...transportState('muted_until_user_taps'), ...echo, playing: false, next: MUTED_NEXT };
    }
    ctx.summarize(fromBar === undefined ? 'started the loop' : `started the loop from bar ${fromBar + 1}`);
    return { ...transportState(res.audio), ...echo };
  };

  const export_midi: Executor<'export_midi'> = (input, ctx) => {
    const name = sanitizeFilename(input.filename);
    let bytes: Uint8Array;
    try {
      bytes = buildSMF(store.get(), { name });
    } catch (e) {
      return fail('export_failed', errorMessage(e));
    }
    const copy = new Uint8Array(bytes.length);
    copy.set(bytes);
    const blob = new Blob([copy], { type: 'audio/midi' });
    const url = URL as unknown as { createObjectURL?: (b: Blob) => string; revokeObjectURL?: (u: string) => void };
    let href: string;
    if (typeof url.createObjectURL === 'function') {
      if (lastObjectUrl && typeof url.revokeObjectURL === 'function') url.revokeObjectURL(lastObjectUrl);
      href = url.createObjectURL(blob);
      lastObjectUrl = href;
    } else {
      href = bytesToDataUrl(copy);
    }
    const filename = `${name}.mid`;
    controller.setDownload(href, filename);
    spotlight(deps.downloadButton(), { label: 'Download MIDI' });
    ctx.summarize(`exported ${filename} (${bytes.length} bytes) — Download button armed`);
    return { ready: true, filename, bytes: bytes.length, tracks: 4, drums_channel: 10, next: EXPORT_NEXT };
  };

  // ---------- recording ----------

  const stop_recording: Executor<'stop_recording'> = (_input, ctx) => {
    if (!controller.isRecording()) {
      return { applied: false, reason: 'not_recording', summary: 'no recording is armed' };
    }
    const rec = controller.stopRecording();
    const bars = rec.bars_touched.length ? ` in bars ${rec.bars_touched.join(', ')}` : '';
    const summary = `recorded ${plural(rec.notes, 'note')} on lead${bars}`;
    ctx.summarize(summary);
    return { applied: true, recorded: { notes: rec.notes, bars_touched: rec.bars_touched }, summary };
  };

  const exec: Executors = {
    get_session,
    get_track,
    suggest_chords,
    set_groove,
    add_chords,
    add_bass,
    add_drums,
    write_notes,
    set_drum_steps,
    set_track,
    clear_track,
    audition,
    undo,
    transport,
    export_midi,
    stop_recording,
  };

  return {
    exec,
    dispose() {
      cancelAudition?.();
      cancelAudition = null;
      const url = URL as unknown as { revokeObjectURL?: (u: string) => void };
      if (lastObjectUrl && typeof url.revokeObjectURL === 'function') url.revokeObjectURL(lastObjectUrl);
      lastObjectUrl = null;
    },
  };
}

// ---------------------------------------------------------------------------
// ToolDefs
// ---------------------------------------------------------------------------

/**
 * Build every `ToolDef` from the frozen schema plus its executor, wrapped so
 * `execute` never throws (SPEC §5: results are plain JSON objects; failures
 * become `{ error: { code: 'execution_failed' | 'aborted', message } }`).
 */
export function createTools(deps: ToolDeps): CreatedTools {
  const { exec, dispose } = createExecutors(deps);
  const defs = {} as Record<ToolName, ToolDef>;
  for (const name of TOOL_NAMES) {
    const schema = TOOLS[name];
    const run = exec[name] as Executor<ToolName>;
    const def: ToolDef = {
      name: schema.name,
      description: schema.description,
      inputSchema: toJSONSchema(schema.inputSchema),
      async execute(input, ctx) {
        try {
          return await run(input as never, ctx);
        } catch (e) {
          if (isAbortError(e) || ctx.signal?.aborted) return fail('aborted', errorMessage(e) || 'aborted');
          return fail('execution_failed', errorMessage(e));
        }
      },
    };
    if (schema.annotations) def.annotations = { ...schema.annotations };
    defs[name] = def;
  }
  return { defs, dispose };
}
